import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { initialChoice: String }

  static targets = [
    // Gate
    "gateCard",
    "gateContinueBtn",
    "choiceCard",
    "redirRetail",
    "redirUnsure",
    "formBlock",
    "clientBadge",
    "badgeIcon",
    "badgeText",
    "b2bNotice",
    "companyField",
    "invoiceEmailField",
    "billingSection",
    "deliveryMethodField",
    "hobbyDeliveryNote",
    // Steps
    "step1Bubble", "step1Label",
    "step2Bubble", "step2Label",
    "step3Bubble", "step3Label",
    // Form fields
    "clientType",
    "orgField",
    "organizationNumber",
    "turnoverField",
    "turnoverLow",
    "turnoverHigh",
    "monthlyTurnoverField",
    "hobbyBlockedCard",
    "formSubmit",
    // Delivery
    "deliveryMethodSelect",
    "deliverySection",
    "deliveryAddress",
    "sameAsMainField",
    "deliveryMap",
    "deliveryLatitude",
    "deliveryLongitude",
    // Phone
    "mainPhone",
    "mainPhoneField"
  ]

  connect() {
    this.gateChoice = null
    this.initializeDeliveryAddress()
    this.initializeDeliveryMethod()
    this.updateMainPhoneDelayed()

    if (this.initialChoiceValue) {
      this.gateChoice = this.initialChoiceValue
      const card = this.choiceCardTargets.find(c => c.dataset.choice === this.initialChoiceValue)
      if (card) card.classList.add(`selected-${this.initialChoiceValue}`)
      this.proceedFromGate()
    }
  }

  // ── Gate ─────────────────────────────────────────────────────────────────

  selectGate(event) {
    const card = event.currentTarget
    const choice = card.dataset.choice

    this.choiceCardTargets.forEach(c => {
      c.className = c.className.replace(/\bselected-\w+/g, '').trim()
    })
    card.classList.add(`selected-${choice}`)

    this.gateChoice = choice
    this.gateContinueBtnTarget.classList.remove('d-none')
  }

  proceedFromGate() {
    if (!this.gateChoice) return

    this.gateCardTarget.classList.add('d-none')
    this.redirRetailTarget.classList.add('d-none')
    this.redirUnsureTarget.classList.add('d-none')
    this.formBlockTarget.classList.add('d-none')

    if (this.gateChoice === 'retail') {
      this.redirRetailTarget.classList.remove('d-none')
      this.setStep(1)
    } else if (this.gateChoice === 'unsure') {
      this.redirUnsureTarget.classList.remove('d-none')
      this.setStep(1)
    } else {
      this.formBlockTarget.classList.remove('d-none')

      const selectedCard = this.choiceCardTargets.find(c => c.dataset.choice === this.gateChoice)
      if (this.gateChoice === 'hobby') {
        this.badgeIconTarget.textContent = '🔧'
      } else {
        this.badgeIconTarget.textContent = '🏚'
      }
      this.badgeTextTarget.textContent = selectedCard?.dataset.badgeLabel || ''

      if (this.gateChoice === 'hobby' && this.hasClientTypeTarget) {
        this.clientTypeTarget.value = 'hobby'
        this.onClientTypeChange()
      } else if (this.hasClientTypeTarget && this.clientTypeTarget.value) {
        this.onClientTypeChange()
      }

      this.updateB2bNotice()
      this.setStep(2)
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  resetGate() {
    this.gateChoice = null
    this.gateCardTarget.classList.remove('d-none')
    this.gateContinueBtnTarget.classList.add('d-none')
    this.redirRetailTarget.classList.add('d-none')
    this.redirUnsureTarget.classList.add('d-none')
    this.formBlockTarget.classList.add('d-none')

    this.choiceCardTargets.forEach(c => {
      c.className = c.className.replace(/\bselected-\w+/g, '').trim()
    })

    if (this.hasClientTypeTarget) this.clientTypeTarget.value = ''
    this.orgFieldTarget.classList.add('d-none')
    this.disableFields(this.orgFieldTarget)
    this.turnoverFieldTarget.classList.add('d-none')
    this.clearTurnoverSelection()
    this.updateB2bNotice()

    this.setStep(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  setStep(n) {
    const bubbles = [this.step1BubbleTarget, this.step2BubbleTarget, this.step3BubbleTarget]
    const labels  = [this.step1LabelTarget,  this.step2LabelTarget,  this.step3LabelTarget]

    bubbles.forEach((el, i) => {
      const step = i + 1
      if (step < n) {
        el.style.background = '#059669'
        el.style.color = '#fff'
      } else if (step === n) {
        el.style.background = '#1a56db'
        el.style.color = '#fff'
      } else {
        el.style.background = '#e5e7eb'
        el.style.color = '#9ca3af'
      }
    })

    labels.forEach((el, i) => {
      const active = i + 1 === n
      el.style.color = active ? '#1a56db' : ''
      el.classList.toggle('text-muted', !active)
      el.classList.toggle('fw-semibold', active)
    })
  }

  // ── Client type / org.nr / turnover ──────────────────────────────────────

  onClientTypeChange() {
    const type = this.clientTypeTarget.value

    if (type === 'hobby') {
      this.orgFieldTarget.classList.add('d-none')
      this.disableFields(this.orgFieldTarget)
      this.turnoverFieldTarget.classList.remove('d-none')
      this.applyHobbyFields(true)
    } else if (type) {
      this.orgFieldTarget.classList.remove('d-none')
      this.enableFields(this.orgFieldTarget)
      this.turnoverFieldTarget.classList.add('d-none')
      this.clearTurnoverSelection()
      this.applyHobbyFields(false)
    } else {
      this.orgFieldTarget.classList.add('d-none')
      this.disableFields(this.orgFieldTarget)
      this.turnoverFieldTarget.classList.add('d-none')
      this.clearTurnoverSelection()
      this.applyHobbyFields(false)
    }

    this.updateB2bNotice()
  }

  // Hobby customers register without a company name or invoice email and can
  // only pick up in person, but they still provide a main address like everyone
  // else (no delivery address though). Business customers see all fields.
  applyHobbyFields(isHobby) {
    this.toggleSection(this.hasCompanyFieldTarget && this.companyFieldTarget, !isHobby)
    this.toggleSection(this.hasInvoiceEmailFieldTarget && this.invoiceEmailFieldTarget, !isHobby)

    if (this.hasDeliveryMethodFieldTarget) {
      this.deliveryMethodFieldTarget.classList.toggle('d-none', isHobby)
    }
    if (this.hasHobbyDeliveryNoteTarget) {
      this.hobbyDeliveryNoteTarget.classList.toggle('d-none', !isHobby)
    }

    if (isHobby) {
      if (this.hasDeliveryMethodSelectTarget) this.deliveryMethodSelectTarget.value = 'personal_pickup'
      this.applyDeliveryMethod('personal_pickup')
      if (this.hasSameAsMainFieldTarget) this.sameAsMainFieldTarget.disabled = true
    } else if (this.hasSameAsMainFieldTarget) {
      this.sameAsMainFieldTarget.disabled = false
    }
  }

  toggleSection(el, visible) {
    if (!el) return
    el.classList.toggle('d-none', !visible)
    if (visible) {
      this.enableFields(el)
    } else {
      this.disableFields(el)
    }
  }

  updateB2bNotice() {
    if (!this.hasB2bNoticeTarget) return
    const type = this.hasClientTypeTarget ? this.clientTypeTarget.value : ''
    const show = this.gateChoice === 'b2b' && type !== 'hobby'
    this.b2bNoticeTarget.classList.toggle('d-none', !show)
  }

  selectTurnoverLow() {
    this.monthlyTurnoverFieldTarget.value = 'low'
    this.turnoverLowTarget.classList.add('selected')
    this.turnoverHighTarget.classList.remove('selected')
    this.applyHobbyBlock(true)
  }

  selectTurnoverHigh() {
    this.monthlyTurnoverFieldTarget.value = 'high'
    this.turnoverHighTarget.classList.add('selected')
    this.turnoverLowTarget.classList.remove('selected')
    this.applyHobbyBlock(false)
    // applyHobbyBlock re-enables every field; re-hide the ones hobby skips.
    this.applyHobbyFields(true)
  }

  applyHobbyBlock(blocked) {
    if (this.hasHobbyBlockedCardTarget) {
      this.hobbyBlockedCardTarget.classList.toggle('d-none', !blocked)
    }
    const form = this.element.querySelector('form')
    if (!form) return
    form.querySelectorAll('input:not([data-honeypot]):not([data-keep-disabled]):not([name="client_registration[locale]"]):not([name="client_registration[monthly_turnover]"]), select, textarea').forEach(el => {
      el.disabled = blocked
    })
    const submitBtn = form.querySelector('[type="submit"]')
    if (submitBtn) submitBtn.disabled = blocked
  }

  clearTurnoverSelection() {
    if (this.hasMonthlyTurnoverFieldTarget) this.monthlyTurnoverFieldTarget.value = ''
    if (this.hasTurnoverLowTarget)  this.turnoverLowTarget.classList.remove('selected')
    if (this.hasTurnoverHighTarget) this.turnoverHighTarget.classList.remove('selected')
    this.applyHobbyBlock(false)
  }

  // ── Delivery method ───────────────────────────────────────────────────────

  initializeDeliveryMethod() {
    if (!this.hasDeliveryMethodSelectTarget) return
    this.applyDeliveryMethod(this.deliveryMethodSelectTarget.value)
  }

  onDeliveryMethodChange(event) {
    this.applyDeliveryMethod(event.target.value)
  }

  applyDeliveryMethod(method) {
    const isPickup = method === 'personal_pickup'
    this.deliverySectionTargets.forEach(el => {
      el.classList.toggle('d-none', isPickup)
      if (isPickup) {
        this.disableFields(el)
      } else {
        const checkbox = document.getElementById('sameAsMain')
        if (el.classList.contains('form-check') || (checkbox && el.contains(checkbox))) return
        this.enableFields(el)
      }
    })
    if (isPickup && this.hasSameAsMainFieldTarget) {
      this.sameAsMainFieldTarget.value = 'true'
    }
    if (!isPickup) {
      this.initializeDeliveryAddress()
    }
  }

  // ── Delivery address ─────────────────────────────────────────────────────

  initializeDeliveryAddress() {
    const checkbox = document.getElementById('sameAsMain')
    if (checkbox && checkbox.checked) {
      this.deliveryAddressTarget.classList.add('d-none')
      this.disableFields(this.deliveryAddressTarget)
    }
  }

  toggleDeliveryAddress(event) {
    const isChecked = event.target.checked

    if (isChecked) {
      this.deliveryAddressTarget.classList.add('d-none')
      this.disableFields(this.deliveryAddressTarget)
      this.sameAsMainFieldTarget.value = 'true'
      this.copyMainAddressToDelivery()
    } else {
      this.deliveryAddressTarget.classList.remove('d-none')
      this.enableFields(this.deliveryAddressTarget)
      this.sameAsMainFieldTarget.value = 'false'
      setTimeout(() => { this.geocodeDeliveryAddress() }, 100)
    }
  }

  copyMainAddressToDelivery() {
    const mainStreet          = document.querySelector('input[name="client_registration[main_address][street]"]')?.value || ''
    const mainHouseNumber     = document.querySelector('input[name="client_registration[main_address][house_number]"]')?.value || ''
    const mainApartmentNumber = document.querySelector('input[name="client_registration[main_address][apartment_number]"]')?.value || ''
    const mainPostalCode      = document.querySelector('input[name="client_registration[main_address][postal_code]"]')?.value || ''
    const mainCity            = document.querySelector('input[name="client_registration[main_address][city]"]')?.value || ''

    const set = (sel, val) => { const el = document.querySelector(sel); if (el) el.value = val }
    set('input[name="client_registration[delivery_addresses][][street]"]',           mainStreet)
    set('input[name="client_registration[delivery_addresses][][house_number]"]',      mainHouseNumber)
    set('input[name="client_registration[delivery_addresses][][apartment_number]"]',  mainApartmentNumber)
    set('input[name="client_registration[delivery_addresses][][postal_code]"]',       mainPostalCode)
    set('input[name="client_registration[delivery_addresses][][city]"]',              mainCity)

    this.geocodeMainAddressForDelivery()
  }

  // ── Geocoding ─────────────────────────────────────────────────────────────

  geocodeMainAddressForDelivery() {
    const street     = this.cleanStreetName(document.querySelector('input[name="client_registration[main_address][street]"]')?.value || '')
    const houseNum   = document.querySelector('input[name="client_registration[main_address][house_number]"]')?.value || ''
    const postalCode = document.querySelector('input[name="client_registration[main_address][postal_code]"]')?.value || ''
    const city       = document.querySelector('input[name="client_registration[main_address][city]"]')?.value || ''

    if (!street || !city) {
      alert('Please fill in street and city fields before updating the map.')
      return
    }

    this.geocode(`${street} ${houseNum}, ${postalCode} ${city}, NO`)
  }

  geocodeDeliveryAddress() {
    const street     = this.cleanStreetName(document.querySelector('input[name="client_registration[delivery_addresses][][street]"]')?.value || '')
    const houseNum   = document.querySelector('input[name="client_registration[delivery_addresses][][house_number]"]')?.value || ''
    const postalCode = document.querySelector('input[name="client_registration[delivery_addresses][][postal_code]"]')?.value || ''
    const city       = document.querySelector('input[name="client_registration[delivery_addresses][][city]"]')?.value || ''

    if (!street || !city) {
      alert('Please fill in street and city fields before updating the map.')
      return
    }

    this.geocode(`${street} ${houseNum}, ${postalCode} ${city}, NO`)
  }

  geocode(address) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`

    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat)
          const lng = parseFloat(data[0].lon)
          const mapController = this.application.getControllerForElementAndIdentifier(this.deliveryMapTarget, 'map')
          if (mapController?.map) {
            mapController.map.setView([lat, lng], 13)
            if (mapController.marker) mapController.marker.setLatLng([lat, lng])
            mapController.updateCoordinates(lat, lng)
          }
        } else {
          alert('Could not find the address on the map. Please check the address or manually place the marker.')
        }
      })
      .catch(() => {
        alert('Error connecting to geocoding service. Please try again later.')
      })
  }

  updateMapMarker() {
    const checkbox = document.getElementById('sameAsMain')
    if (checkbox && checkbox.checked) {
      this.geocodeMainAddressForDelivery()
    } else {
      this.geocodeDeliveryAddress()
    }
  }

  cleanStreetName(street) {
    const prefixes = ['ul.', 'ul ', 'ulica ', 'str.', 'str ', 'street ', 'ave.', 'ave ', 'avenue ', 'gt.', 'gt ', 'gate ', 'vei ', 'veien ']
    let cleaned = street.trim()
    for (const prefix of prefixes) {
      if (cleaned.toLowerCase().startsWith(prefix)) {
        cleaned = cleaned.substring(prefix.length).trim()
        break
      }
    }
    return cleaned
  }

  // ── Org number validation ─────────────────────────────────────────────────

  validateOrgNumber() {
    const orgNumber = this.organizationNumberTarget.value.replace(/\D/g, '')

    if (orgNumber.length === 0) { this.clearOrgNumberValidation(); return }
    if (orgNumber.length !== 9) { this.showOrgNumberError('Organization number must be 9 digits'); return }

    if (this.validateNorwegianOrgNumber(orgNumber)) {
      this.showOrgNumberSuccess()
    } else {
      this.showOrgNumberError('Invalid Norwegian organization number')
    }
  }

  validateNorwegianOrgNumber(orgNumber) {
    const weights = [3, 2, 7, 6, 5, 4, 3, 2]
    const digits  = orgNumber.split('').map(Number)
    const sum     = digits.slice(0, 8).reduce((acc, d, i) => acc + d * weights[i], 0)
    const remainder = sum % 11
    const checkDigit = remainder === 0 ? 0 : 11 - remainder
    if (checkDigit === 10) return false
    return checkDigit === digits[8]
  }

  showOrgNumberError(message) {
    const input   = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3') || input.parentElement
    input.classList.remove('is-valid')
    input.classList.add('is-invalid')
    let feedback = wrapper.querySelector('.invalid-feedback')
    if (!feedback) {
      feedback = document.createElement('div')
      feedback.className = 'invalid-feedback'
      input.after(feedback)
    }
    feedback.textContent = message
  }

  showOrgNumberSuccess() {
    const input   = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3') || input.parentElement
    input.classList.remove('is-invalid')
    input.classList.add('is-valid')
    wrapper.querySelector('.invalid-feedback')?.remove()
  }

  clearOrgNumberValidation() {
    const input   = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3') || input.parentElement
    input.classList.remove('is-invalid', 'is-valid')
    wrapper.querySelector('.invalid-feedback')?.remove()
  }

  // ── Phone ─────────────────────────────────────────────────────────────────

  updateMainPhone() {
    if (!this.hasMainPhoneTarget || !this.hasMainPhoneFieldTarget) return
    const phoneController = this.application.getControllerForElementAndIdentifier(
      this.mainPhoneTarget.closest('[data-controller~="phone-number"]'),
      'phone-number'
    )
    if (phoneController?.iti) {
      this.mainPhoneFieldTarget.value = phoneController.iti.getNumber()
    }
  }

  updateMainPhoneDelayed() {
    setTimeout(() => { this.updateMainPhone() }, 500)
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  enableFields(container) {
    container.querySelectorAll('input, select, textarea').forEach(el => {
      if (el.dataset.keepDisabled !== undefined) return
      el.disabled = false
    })
  }

  disableFields(container) {
    container.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true)
  }
}
